/*!
 * @utiljs/functional 0.6.2 (http://git.sankuai.com/projects/WM/repos/utiljs/browse/packages/functional/)
 * API http://git.sankuai.com/projects/WM/repos/utiljs/browse/packages/functional/doc/api.md
 * Copyright 2017-2019 美团mfe. All Rights Reserved
 * Licensed under MIT (http://git.sankuai.com/projects/WM/repos/utiljs/browse/packages/functional/LICENSE)
 */

import { type } from '@utiljs/type';

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function pipe() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return fns.reduce(function (prev, fn) {
      return fn.apply(void 0, _toConsumableArray([].concat(prev)));
    }, args);
  };
}
function compose() {
  for (var _len3 = arguments.length, fns = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    fns[_key3] = arguments[_key3];
  }

  return function () {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }

    return fns.reduceRight(function (prev, fn) {
      return fn.apply(void 0, _toConsumableArray([].concat(prev)));
    }, args);
  };
}
function currying(func) {
  for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
    args[_key5 - 1] = arguments[_key5];
  }

  return func.bind.apply(func, [null].concat(args));
}
function curry(func) {
  var len = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : func.length;

  if (type(func) !== 'function') {
    throw new TypeError('curry first param must be a function');
  }

  len = Math.ceil(len);

  if (isNaN(len)) {
    throw new TypeError('curry second param must be a number');
  }

  function partial(func, argsList, argsLen) {
    if (argsList.length >= argsLen) {
      return func.apply(this, argsList);
    }

    return function () {
      for (var _len6 = arguments.length, args = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
        args[_key6] = arguments[_key6];
      }

      return partial.call(this, func, argsList.concat(args), argsLen);
    };
  }

  return partial(func, [], len);
}
function bool(flag) {
  return function () {
    return !!flag;
  };
}
function and() {
  for (var _len7 = arguments.length, fns = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
    fns[_key7] = arguments[_key7];
  }

  return function () {
    for (var _len8 = arguments.length, args = new Array(_len8), _key8 = 0; _key8 < _len8; _key8++) {
      args[_key8] = arguments[_key8];
    }

    return fns.every(function (fn) {
      return fn.apply(void 0, args);
    });
  };
}
function or() {
  for (var _len9 = arguments.length, fns = new Array(_len9), _key9 = 0; _key9 < _len9; _key9++) {
    fns[_key9] = arguments[_key9];
  }

  return function () {
    for (var _len10 = arguments.length, args = new Array(_len10), _key10 = 0; _key10 < _len10; _key10++) {
      args[_key10] = arguments[_key10];
    }

    return fns.some(function (fn) {
      return fn.apply(void 0, args);
    });
  };
}
function not(fn) {
  return function () {
    return !fn.apply(void 0, arguments);
  };
}
function debounce(func) {
  var wait = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

  if (type(func) !== 'function') {
    throw new TypeError('the first param `func` must be a function');
  }

  wait = Math.round(wait, 10);
  var timerId;
  return function debounced() {
    var _this = this;

    for (var _len11 = arguments.length, args = new Array(_len11), _key11 = 0; _key11 < _len11; _key11++) {
      args[_key11] = arguments[_key11];
    }

    if (timerId) clearTimeout(timerId);
    timerId = setTimeout(function () {
      func.apply(_this, args);
    }, wait);
  };
}
function once(func) {
  if (type(func) !== 'function') {
    throw new TypeError('the param `func` must be a function');
  }

  var flag = 0;
  return function () {
    if (flag === 0) {
      for (var _len12 = arguments.length, args = new Array(_len12), _key12 = 0; _key12 < _len12; _key12++) {
        args[_key12] = arguments[_key12];
      }

      func.apply.apply(func, [this].concat(args));
    }

    flag = 1;
  };
}
function throttle(func, time) {
  if (type(func) !== 'function') {
    throw new TypeError('the first param `func` must be a function');
  }

  var args;
  var timer = null;
  var lastTriggerTime = 0; // 首次触发执行

  function self() {
    var _this2 = this;

    for (var _len13 = arguments.length, a = new Array(_len13), _key13 = 0; _key13 < _len13; _key13++) {
      a[_key13] = arguments[_key13];
    }

    args = a;
    var ct = Date.now();
    var diff = ct - lastTriggerTime;
    if (timer) clearTimeout(timer);

    if (diff > time) {
      func.apply(this, args);
      lastTriggerTime = ct;
      return;
    }

    timer = setTimeout(function () {
      lastTriggerTime = Date.now();
      func.apply(_this2, args);
      timer = null;
    }, time - diff);
  }

  self.clear = function () {
    if (timer) clearTimeout(timer);
  };

  return self;
}

export { pipe, compose, currying, curry, bool, and, or, not, debounce, once, throttle };
